import type {
  FormatDateTimeOptions,
  FormatDateTimeResult,
  TimeFormat,
} from 'shared/types/date-time-format'

const TIME_FORMAT_INTL = {
  'H:i': { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' },
  'h:i A': { hour: '2-digit', minute: '2-digit', hourCycle: 'h12' },
  'H:i:s': { hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' },
  'h:i:s A': { hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h12' },
} as const satisfies Record<
  TimeFormat,
  Pick<Intl.DateTimeFormatOptions, 'hour' | 'hourCycle' | 'minute' | 'second'>
>

/**
 * Unified Intl-based date/time formatter.
 *
 * - Without `timeFormat` → date only (e.g. "28/05/2026")
 * - With `timeFormat` → date + time (e.g. "28/05/2026, 14:30")
 *
 * Returns both formatted string and title with timezone identifier.
 * AM/PM casing is normalized via `formatToParts`.
 */
export const formatDateTimeUtil = (
  date: Date | number | string,
  options: FormatDateTimeOptions = {},
): FormatDateTimeResult => {
  const d = date instanceof Date ? date : new Date(date)

  // dateFormat determines date order (d/m/Y → en-GB, m/d/Y → en-US)
  // locale is ignored when dateFormat is present
  const locale = options.dateFormat
    ? options.dateFormat === 'm/d/Y'
      ? 'en-US'
      : 'en-GB'
    : (options.locale ?? 'en-GB')

  const timeZone = options.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone

  const formatter = new Intl.DateTimeFormat(locale, {
    day: options.day ?? '2-digit',
    month: options.month ?? '2-digit',
    year: options.year ?? 'numeric',
    timeZone,
    ...(options.timeFormat && TIME_FORMAT_INTL[options.timeFormat]),
  })

  const isAmPm = options.timeFormat === 'h:i A' || options.timeFormat === 'h:i:s A'

  const formatted = isAmPm
    ? formatter
        .formatToParts(d)
        .map(part => (part.type === 'dayPeriod' ? part.value.toUpperCase() : part.value))
        .join('')
    : formatter.format(d)

  return {
    formatted,
    title: `${formatted} ${timeZone}`,
  }
}
